import React, { useState, useRef, useMemo } from "react";
import { observer } from "mobx-react";
import { Autocomplete, Select, Button } from "@ann/web-components";
import {
  FiltersContainer,
  FiltersLeftContent,
  FiltersRightContent,
  SearchContainer
} from "./Filters.styled";

const Filters = observer(() => {
  const [searchValue, setSearchValue] = useState("");
  const [searchKey, setSearchKey] = useState(0);
  const [searchTermDisposition, setSearchTermDisposition] = useState("");
  const [searchTermLastUpdate, setSearchTermLastUpdate] = useState("");
  const [searchTermCallType, setSearchTermCallType] = useState("");
  const [filtersKey, setFiltersKey] = useState(0);
  const autocompleteRef = useRef(null);

  // Filter options
  const dispositionOptions = [
    { label: "Answered", value: "answered" },
    { label: "No Answer", value: "no_answer" },
    { label: "Busy", value: "busy" },
    { label: "Failed", value: "failed" },
    { label: "Voicemail", value: "voicemail" },
    { label: "Call Back", value: "call_back" },
    { label: "Wrong Number", value: "wrong_number" },
    { label: "Not Interested", value: "not_interested" },
  ];

  const lastUpdateOptions = [
    { label: "Last 7 days", value: "last_7_days" },
    { label: "Last 30 days", value: "last_30_days" },
    { label: "This month", value: "this_month" },
    { label: "This year", value: "this_year" },
  ];

  const callTypeOptions = [
    { label: "Inbound", value: "inbound" },
    { label: "Outbound", value: "outbound" },
    { label: "Internal", value: "internal" },
    { label: "Conference", value: "conference" },
    { label: "Transfer", value: "transfer" },
    { label: "Forward", value: "forward" },
    { label: "Callback", value: "callback" },
    { label: "Voicemail", value: "voicemail" },
  ];

  // Filtered options based on search terms
  const filteredDisposition = useMemo(() => {
    if (!searchTermDisposition.trim()) return dispositionOptions;
    const searchLower = searchTermDisposition.toLowerCase();
    return dispositionOptions.filter(
      (option) =>
        option.label.toLowerCase().includes(searchLower) ||
        option.value.toLowerCase().includes(searchLower)
    );
  }, [searchTermDisposition]);

  const filteredLastUpdate = useMemo(() => {
    if (!searchTermLastUpdate.trim()) return lastUpdateOptions;
    const searchLower = searchTermLastUpdate.toLowerCase();
    return lastUpdateOptions.filter(
      (option) =>
        option.label.toLowerCase().includes(searchLower) ||
        option.value.toLowerCase().includes(searchLower)
    );
  }, [searchTermLastUpdate]);

  const filteredCallType = useMemo(() => {
    if (!searchTermCallType.trim()) return callTypeOptions;
    const searchLower = searchTermCallType.toLowerCase();
    return callTypeOptions.filter(
      (option) =>
        option.label.toLowerCase().includes(searchLower) ||
        option.value.toLowerCase().includes(searchLower)
    );
  }, [searchTermCallType]);

  const handleSearchChange = (value) => {
    setSearchValue(value);
    // Here you can add your search logic or call a callback function
    // to update the parent component's state
  };

  const handleSearchClear = () => {
    setSearchValue("");
    setSearchKey((prev) => prev + 1);

    if (autocompleteRef.current) {
      const input = autocompleteRef.current.querySelector("input");
      if (input) {
        input.value = "";
        input.dispatchEvent(new Event("input", { bubbles: true }));
      }
    }
  };

  const handleDispositionChange = (items) => {
    // Handle disposition filter changes
    console.log("Disposition filter changed:", items);
  };

  const handleLastUpdateChange = (items) => {
    // Handle last update filter changes
    console.log("Last update filter changed:", items);
  };

  const handleCallTypeChange = (items) => {
    // Handle call type filter changes
    console.log("Call type filter changed:", items);
  };

  const handleClearAllFilters = () => {
    setSearchValue("");
    setSearchTermDisposition("");
    setSearchTermLastUpdate("");
    setSearchTermCallType("");
    setSearchKey((prev) => prev + 1);
    setFiltersKey((prev) => prev + 1);

    // Clear autocomplete input
    if (autocompleteRef.current) {
      const input = autocompleteRef.current.querySelector("input");
      if (input) {
        input.value = "";
        input.dispatchEvent(new Event("input", { bubbles: true }));
      }
    }
  };

  const emptyOrValue = (arr) => (arr.length > 0 ? arr : undefined);

  return (
    <FiltersContainer>
      <FiltersLeftContent>
        <SearchContainer>
          <Autocomplete
            key={searchKey}
            ref={autocompleteRef}
            value={searchValue}
            onChange={handleSearchChange}
            onSearch={() => { }}
            onSuggestionSelect={() => { }}
            onClear={handleSearchClear}
            placeholder="Search calls..."
            suggestions={[
              "Incoming call",
              "Outgoing call",
              "Missed call",
              "Voicemail",
              "Conference call",
              "Support call",
              "Sales call",
            ]}
          />
        </SearchContainer>
        <Select
          key={`disposition-filter-${filtersKey}`}
          label="Disposition"
          placeholder="Disposition"
          onChange={handleDispositionChange}
          size="small"
          value={undefined}
          width="auto"
          minWidth="60px"
        >
          <Select.Menu>
            {filteredDisposition.map((disposition) => (
              <Select.Item
                key={disposition.value}
                value={disposition.value}
                label={disposition.label}
              >
                {disposition.label}
              </Select.Item>
            ))}
          </Select.Menu>
        </Select>
        <Select
          key={`last-update-filter-${filtersKey}`}
          label="Last Update"
          placeholder="Last Update"
          onChange={handleLastUpdateChange}
          size="small"
          value={undefined}
          width="auto"
          minWidth="60px"
        >
          <Select.Menu>
            {filteredLastUpdate.map((option) => (
              <Select.Item
                key={option.value}
                value={option.value}
                label={option.label}
              >
                {option.label}
              </Select.Item>
            ))}
          </Select.Menu>
        </Select>
        <Select
          key={`call-type-filter-${filtersKey}`}
          label="Call Type"
          placeholder="Call Type"
          onChange={handleCallTypeChange}
          size="small"
          value={undefined}
          width="auto"
          minWidth="60px"
        >
          <Select.Menu>
            {filteredCallType.map((callType) => (
              <Select.Item
                key={callType.value}
                value={callType.value}
                label={callType.label}
              >
                {callType.label}
              </Select.Item>
            ))}
          </Select.Menu>
        </Select>
        <Button
          icon=""
          iconLeft=""
          iconRight=""
          label="Clear Filters"
          size="medium"
          type="secondary"
          onClick={handleClearAllFilters}
        />
      </FiltersLeftContent>
    </FiltersContainer>
  );
});

export default Filters; 